// A BOX'S PLACE IN ITS ACCOUNT'S SEAT ORDER — the one answer the assigner, the lane demand
// and the box read model all give (2026-11-03).
//
// An account's paid seats (`aiDevs`) cap how many of its ENROLLED (`org`) ai-dev boxes run at
// once, and the boxes admitted are the FIRST `aiDevs` live ones oldest-enrolled first
// (`@developerz/db` `listActiveAiDevRunners`, the seat-drain's own keep order). A box past
// that rank is refused by the claim whatever the queue holds. Measured on tesote 2026-11-03:
// 12 seats, 21 online org boxes, so 9 boxes idled beside a deep queue with nothing saying
// why — the assigner counted their account's rows as claimable demand and its `rank_excess`
// refusal was never logged. This value object is what each of those surfaces now reads.

/** Where one box sits in its account's seat order. */
export type SeatOrder = {
  /** 1-based position among the account's live ai-dev boxes; `null` when not among them. */
  readonly rank: number | null;
  /** The account's seats (`aiDevs`); `Infinity` for an unlimited fleet. */
  readonly seats: number;
  /** Inside the seat count: the claim's rank gate admits it. */
  readonly within: boolean;
};

/**
 * The box's seat order — `within` exactly when the assigner's rank gate (`withinSeatCap`,
 * `apps/worker` `dispatch/seat-cap.ts`) admits it: ranked inside the first `aiDevs`.
 */
export function seatOrderOf(input: {
  readonly runnerId: string;
  readonly aiDevs: number;
  readonly activeAiDevRunnerIds: readonly string[];
}): SeatOrder {
  const at = input.activeAiDevRunnerIds.indexOf(input.runnerId);
  const rank = at === -1 ? null : at + 1;
  return { rank, seats: input.aiDevs, within: rank !== null && rank <= input.aiDevs };
}

/**
 * The sentence an operator reads for a box OUTSIDE its seat count — `outside seat count
 * (14 of 12 seats)`: the box is 14th in enrollment order on an account with 12 seats. `null`
 * for a box inside the count, or one not in the order at all (not a live ai-dev box, so the
 * rank gate does not speak for it).
 */
export function seatOrderNote(order: SeatOrder): string | null {
  if (order.within || order.rank === null) return null;
  const seats = order.seats === 1 ? '1 seat' : `${order.seats} seats`;
  return `outside seat count (${order.rank} of ${seats})`;
}
